import { Grid } from '@mui/material';

import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import { useJobPeriodMutation } from 'api/hooks/jobmarket/jobperiods';
import { MultistepForm } from 'app/components';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { JobOffering, JobPeriod } from 'types/AppTypes';

import SelectJobOffer from './components/SelectJobOffer';
import SelectJobPeriods from './components/SelectJobPeriods';
import { VerifyJobPeriods } from './components/VerifyJobPeriods';
import {
  jobOfferDeactivationModalInitialValues,
  jobOfferDeactivationModalValidationSchema
} from './validation';

interface Props {
  closeHandler: Function;
  jobOfferings?: boolean;
  jobOffering?: JobOffering & { jobPeriods: JobPeriod[] };
}

export const JobOfferDeactivationModal = ({ jobOffering, jobOfferings, closeHandler }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { deactivate } = useJobPeriodMutation();

  const handleSaveClick = async values => {
    try {
      if (deactivate.isPending) {
        return;
      }
      await deactivate.mutateAsync({ jobPeriodIds: values.jobPeriodIds });
      closeHandler();
      dispatch(
        AlertActions.success(
          t(translations.pages.deactivateJobOffers.deactivateJobPeriods.success())
        )
      );
    } catch (err) {
      dispatch(
        AlertActions.error(t(translations.pages.deactivateJobOffers.deactivateJobPeriods.failed()))
      );
    }
  };

  const ModuleJobOfferStep = ({ step }) => {
    switch (step) {
      case 0:
        return <SelectJobPeriods />;
      case 1:
        return <VerifyJobPeriods />;
      default:
        return null;
    }
  };

  const ModuleJobOfferingsStep = ({ step }) => {
    switch (step) {
      case 0:
        return <SelectJobOffer />;
      case 1:
        return <SelectJobPeriods />;
      case 2:
        return <VerifyJobPeriods />;
      default:
        return null;
    }
  };

  const initialValues = jobOfferDeactivationModalInitialValues(
    !!jobOffering
      ? {
          ...jobOffering,
          jobPeriods: jobOffering.jobPeriods.filter(
            jp => !jp.isCanceled && new Date(jp.endDate) > new Date()
          )
        }
      : null
  );

  let steps;
  if (!jobOfferings && !!jobOffering) {
    steps = [
      t(translations.pages.deactivateJobOffers.steps.jobPeriods()),
      t(translations.pages.deactivateJobOffers.steps.verify())
    ];
  } else if (!!jobOfferings) {
    steps = [
      t(translations.pages.deactivateJobOffers.steps.jobOfferings()),
      t(translations.pages.deactivateJobOffers.steps.jobPeriods()),
      t(translations.pages.deactivateJobOffers.steps.verify())
    ];
  }

  return (
    <Grid item style={{ padding: '0 3em' }}>
      <Formik
        initialValues={initialValues}
        validationSchema={yup.object().shape(jobOfferDeactivationModalValidationSchema(t))}
        onSubmit={handleSaveClick}
        validateOnMount
      >
        <MultistepForm
          submitting={deactivate.isPending}
          steps={steps}
          onCancel={closeHandler}
          isStepperClickable
        >
          {step => (
            <Grid container justifyContent={'center'}>
              {!!jobOffering ? (
                <ModuleJobOfferStep step={step} />
              ) : (
                <ModuleJobOfferingsStep step={step} />
              )}
            </Grid>
          )}
        </MultistepForm>
      </Formik>
    </Grid>
  );
};
